// Secure Message Service - Handles storage and retrieval of encrypted messages
import { encryptionService, EncryptedMessage } from '../utils/encryption'
import { messagingPatternDetection } from './messagingPatternDetection'

export interface SecureConversation {
  id: string
  participants: string[]
  lastMessage?: EncryptedMessage
  lastActivity: number
  isPrivateMode: boolean
  title?: string
}

export interface DecryptedMessage {
  id: string
  content: string
  senderId: string
  receiverId: string
  timestamp: number
  isPrivateMode: boolean
  canDecrypt: boolean
}

class SecureMessageService {
  private conversations: Map<string, SecureConversation> = new Map()
  private messages: Map<string, EncryptedMessage[]> = new Map()

  constructor() {
    this.loadFromStorage()
    this.initializeEncryption()
  }

  private async initializeEncryption() {
    await encryptionService.initializeMasterKey()
  }

  private loadFromStorage() {
    const conversationsData = localStorage.getItem('secure_conversations')
    const messagesData = localStorage.getItem('secure_messages')

    if (conversationsData) {
      const conversations = JSON.parse(conversationsData)
      this.conversations = new Map(Object.entries(conversations))
    }

    if (messagesData) {
      const messages = JSON.parse(messagesData)
      this.messages = new Map(Object.entries(messages))
    }
  }

  private saveToStorage() {
    const conversationsObj = Object.fromEntries(this.conversations)
    const messagesObj = Object.fromEntries(this.messages)

    localStorage.setItem('secure_conversations', JSON.stringify(conversationsObj))
    localStorage.setItem('secure_messages', JSON.stringify(messagesObj))
  }

  // Create or get conversation between users
  async createConversation(user1Id: string, user2Id: string, isPrivateMode = false): Promise<string> {
    const conversationId = this.getConversationId(user1Id, user2Id)
    
    if (!this.conversations.has(conversationId)) {
      const conversation: SecureConversation = {
        id: conversationId,
        participants: [user1Id, user2Id].sort(),
        lastActivity: Date.now(),
        isPrivateMode
      }
      
      this.conversations.set(conversationId, conversation)
      this.messages.set(conversationId, [])

      // Generate key pairs for private mode
      if (isPrivateMode) {
        try {
          await encryptionService.generateUserKeyPair(user1Id)
          await encryptionService.generateUserKeyPair(user2Id)
        } catch (error) {
          console.log('Key pairs may already exist')
        }
      }

      this.saveToStorage()
    }

    return conversationId
  }

  // Send encrypted message
  async sendMessage(senderId: string, receiverId: string, content: string, isPrivateMode = false): Promise<EncryptedMessage> {
    const conversationId = await this.createConversation(senderId, receiverId, isPrivateMode)
    
    let encryptedData: { encryptedContent: string; iv: string; salt?: string }

    if (isPrivateMode) {
      encryptedData = await encryptionService.encryptPrivateMessage(content, receiverId)
    } else {
      encryptedData = await encryptionService.encryptRegularMessage(content)
    }

    const message: EncryptedMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      encryptedContent: encryptedData.encryptedContent,
      iv: encryptedData.iv,
      salt: encryptedData.salt,
      isPrivateMode,
      timestamp: Date.now(),
      senderId,
      receiverId
    }

    // Add to messages
    const conversationMessages = this.messages.get(conversationId) || []
    conversationMessages.push(message)
    this.messages.set(conversationId, conversationMessages)

    // Update conversation
    const conversation = this.conversations.get(conversationId)!
    conversation.lastMessage = message
    conversation.lastActivity = Date.now()
    this.conversations.set(conversationId, conversation)

    // Analyze message for suspicious patterns
    try {
      // Check if receiver is a vendor
      const users = JSON.parse(localStorage.getItem('users') || '[]')
      const receiverUser = users.find((u: any) => u.id === receiverId)
      const isVendorContact = receiverUser?.role === 'verified_vendor'
      
      // Analyze the message for patterns (using original content before encryption)
      await messagingPatternDetection.analyzeMessage(senderId, receiverId, content, isVendorContact)
    } catch (error) {
      console.error('Failed to analyze message pattern:', error)
    }

    this.saveToStorage()
    return message
  }

  // Get decrypted messages for a conversation
  async getMessages(conversationId: string, currentUserId: string): Promise<DecryptedMessage[]> {
    const messages = this.messages.get(conversationId) || []
    const decryptedMessages: DecryptedMessage[] = []

    for (const message of messages) {
      try {
        let content: string
        let canDecrypt = true

        if (message.isPrivateMode) {
          // Only sender and receiver can decrypt private messages
          if (message.senderId === currentUserId) {
            content = await encryptionService.decryptPrivateMessage(
              message.encryptedContent, 
              message.iv, 
              message.salt!, 
              currentUserId
            )
          } else if (message.receiverId === currentUserId) {
            content = await encryptionService.decryptPrivateMessage(
              message.encryptedContent, 
              message.iv, 
              message.salt!, 
              currentUserId
            )
          } else {
            content = '[Encrypted - Private Mode]'
            canDecrypt = false
          }
        } else {
          // Regular messages can be decrypted by participants
          content = await encryptionService.decryptRegularMessage(message.encryptedContent, message.iv)
        }

        decryptedMessages.push({
          id: message.id,
          content,
          senderId: message.senderId,
          receiverId: message.receiverId,
          timestamp: message.timestamp,
          isPrivateMode: message.isPrivateMode,
          canDecrypt
        })
      } catch (error) {
        console.error('Failed to decrypt message:', error)
        decryptedMessages.push({
          id: message.id,
          content: '[Failed to decrypt]',
          senderId: message.senderId,
          receiverId: message.receiverId,
          timestamp: message.timestamp,
          isPrivateMode: message.isPrivateMode,
          canDecrypt: false
        })
      }
    }

    return decryptedMessages.sort((a, b) => a.timestamp - b.timestamp)
  }

  // Get all conversations for a user
  getUserConversations(userId: string): SecureConversation[] {
    return Array.from(this.conversations.values())
      .filter(conv => conv.participants.includes(userId))
      .sort((a, b) => b.lastActivity - a.lastActivity)
  }

  // Admin function to decrypt regular messages (for legal compliance)
  async adminDecryptMessage(messageId: string): Promise<string | null> {
    for (const messages of this.messages.values()) {
      const message = messages.find(m => m.id === messageId)
      if (message) {
        return await encryptionService.adminDecryptMessage(message)
      }
    }
    return null
  }

  // Get conversation between two users
  private getConversationId(user1Id: string, user2Id: string): string {
    return [user1Id, user2Id].sort().join('_')
  }

  // Toggle private mode for existing conversation
  async togglePrivateMode(conversationId: string, isPrivateMode: boolean): Promise<void> {
    const conversation = this.conversations.get(conversationId)
    if (conversation) {
      conversation.isPrivateMode = isPrivateMode
      
      // If enabling private mode, ensure key pairs exist
      if (isPrivateMode) {
        for (const userId of conversation.participants) {
          try {
            await encryptionService.generateUserKeyPair(userId)
          } catch (error) {
            console.log('Key pair may already exist for user:', userId)
          }
        }
      }
      
      this.conversations.set(conversationId, conversation)
      this.saveToStorage()
    }
  }

  // Get conversation by ID
  getConversation(conversationId: string): SecureConversation | undefined {
    return this.conversations.get(conversationId)
  }

  // Search conversations (metadata only, not content)
  searchConversations(userId: string, query: string): SecureConversation[] {
    return this.getUserConversations(userId).filter(conv => 
      conv.title?.toLowerCase().includes(query.toLowerCase()) ||
      conv.participants.some(p => p.toLowerCase().includes(query.toLowerCase()))
    )
  }
}

export const secureMessageService = new SecureMessageService()